import { useRouter } from 'next/router';
import type { KeyboardEvent } from 'react';
import type { GlycerinSuggestionType } from '@packages/tracking';
import { useRef, useState, Suspense, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Search32 } from '@packages/themes/icons';
import {
  Box,
  CloseIconButton,
  IconButton,
  TextField,
  Notification,
  MenuList,
  ErrorBoundary,
  CircularProgress,
  useDebounce,
} from '@packages/shared';
import { useSession } from '@packages/utilities';

import { useSearchRoute } from '../../utils';
import { SearchHistory } from './SearchHistory';
import { SearchSuggestions } from './SearchSuggestions';
import { useSearchHistoryUpdate } from './useSearchHistoryUpdate';
import { useSearchFieldTracking } from './useSearchFieldTracking';
import type { Result, Suggestion } from './types';

const messages = defineMessages({
  placeholder: { id: 'searchbar.placeholder', defaultMessage: 'Lieblingsartikel suchen...' },
  ariaLabelInput: {
    id: 'searchbar.input.aria-label',
    defaultMessage: 'suchen-eingabe',
  },
  ariaLabelResetButton: {
    id: 'searchbar.resetbutton.aria-label',
    defaultMessage: 'zurücksetzen',
  },
  ariaLabelButton: {
    id: 'searchbar.button.aria-label',
    defaultMessage: 'suchen-button',
  },
  searchBarEmptyAlert: {
    id: 'searchbar.invalid.empty',
    defaultMessage: 'Bitte geben Sie zunächst Ihren Suchbegriff ein.',
  },
});

export const INITIAL_INPUT_VALUE = '';

const LoadingFallback = () => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      right: 52,
      zIndex: 'appBar',
      display: 'flex',
      transform: 'translateY(-50%)',
      bgcolor: 'common.white',
    }}
  >
    <CircularProgress size="s" />
  </Box>
);

export interface SearchFieldProps {
  /**
   * Sets focus on mounting
   * @default false
   */
  autofocus?: boolean;
  /** If `true`, don't show suggestions or history, and don't reflect current route in input
   * @default false
   * */
  isolated?: boolean;
  onSearch?: () => void;
}

/** Integrated search textfield component with iconbutton used in header */
export const SearchField = ({
  autofocus = false,
  isolated = false,
  onSearch,
}: SearchFieldProps) => {
  const { dispatchSearchGtmEvent } = useSearchFieldTracking();
  const router = useRouter();
  const searchQuery = useSearchRoute();

  const initialValue = searchQuery && !isolated ? searchQuery : INITIAL_INPUT_VALUE;

  const [focused, setFocused] = useState(false);
  // position of with keyboard selected item
  const [cursor, setCursor] = useState(-1);
  // value of search input
  const [value, setValue] = useState(initialValue);
  const [displayValue, setDisplayValue] = useState(initialValue);

  useSearchHistoryUpdate(searchQuery);
  const debouncedValue = useDebounce(value);
  // show alert when search triggered and input empty
  const [showAlert, setShowAlert] = useState(false);
  const inputEl = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const resetCursor = () => setCursor(-1);

  // INFO: needs to get the jwt token here and provide to the popover components to avoid suspense + swr anti-pattern that the key can be null. update when swr completly implements suspense concept in further versions
  const { jwtToken } = useSession();

  const focus = () => {
    setFocused(true);
    inputEl.current?.focus();
  };
  const blur = () => {
    setFocused(false);
    inputEl.current?.blur();
  };

  const updateInput = (newValue: string) => {
    resetCursor();
    setValue(newValue);
    setDisplayValue(newValue);
  };

  const externalChangeValue = (newValue: string, displayOnly: boolean = true) => {
    setDisplayValue(newValue);

    if (!displayOnly) {
      setValue(newValue);
    }
  };

  const hideAlert = () => setShowAlert(false);

  // if input is empty show alert, else go to search route
  const search = () => {
    if (!displayValue) {
      setShowAlert(true);
      return;
    }

    router.push(
      // should work same as in the "old" shop system
      `/s/${encodeURIComponent(displayValue.trim()).replace(/%20/g, '+')}/`,
    );
    dispatchSearchGtmEvent(value || displayValue);
    onSearch?.();
  };

  // handle keyboard item selection in history and suggestions
  const keyHandler = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => c + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c === -1 ? c : c - 1));
    } else if (e.key === 'Enter') {
      const selectedItem = document.getElementById('selected-item');
      if (selectedItem) {
        selectedItem.click();
      } else {
        search();
        blur();
      }
    }
  };

  const clearInputAndFocus = () => {
    setValue(INITIAL_INPUT_VALUE);
    setDisplayValue(INITIAL_INPUT_VALUE);
    resetCursor();
    focus();
  };

  // A change in `initialValue` means a change in the route
  // In this case we want to reset the input and blur, similar to how everything is on initial render
  useEffect(() => {
    setValue(initialValue);
    setDisplayValue(initialValue);
    resetCursor();
    blur();
  }, [initialValue]);

  // A change in `autofocus` should focus the input if need be
  // Caution: This effect needs to run after the previous effect, which blurs the input
  // Otherwise the call of `blur` overrides the call of `focus`
  useEffect(() => {
    if (autofocus) {
      focus();
    }
  }, [autofocus]);

  const relevantPopup =
    focused && (debouncedValue === INITIAL_INPUT_VALUE ? 'history' : 'suggestions');

  return (
    <>
      <Notification open={showAlert} onClose={hideAlert} severity="error" autoHideDuration={2000}>
        {formatMessage(messages.searchBarEmptyAlert)}
      </Notification>
      <Box
        sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
        data-testid="searchfield-wrapper"
        onBlur={blur}
        onFocus={focus}
      >
        <TextField
          sx={{
            pt: 0,
            fieldset: {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderColor: focused ? 'primary.main' : 'grey.dark',
            },
          }}
          onKeyDown={keyHandler}
          value={displayValue}
          fullWidth
          onChange={(event) => updateInput(event.target.value)}
          ref={inputEl}
          inputProps={{
            'data-testid': 'searchfield-input',
            'aria-label': formatMessage(messages.ariaLabelInput),
            enterKeyHint: 'search',
          }}
          // eslint-disable-next-line react/jsx-no-duplicate-props
          InputProps={{
            autoComplete: 'off',
            endAdornment: (value || displayValue) && (
              <CloseIconButton
                onClick={clearInputAndFocus}
                data-testid="searchfield-reset-button"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    clearInputAndFocus();
                  }
                }}
                sx={{ color: 'text.primary' }}
                aria-label={formatMessage(messages.ariaLabelResetButton)}
              />
            ),
          }}
          placeholder={formatMessage(messages.placeholder)}
        />
        <IconButton
          icon={<Search32 />}
          size="large"
          sx={{
            flexShrink: 0,
            ml: '-1px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderColor: focused ? 'primary.main' : 'grey.dark',
            padding: 'inherit',
            '.MuiButton-startIcon > *:nth-of-type(1)': {
              fontSize: 32,
            },
          }}
          color={focused ? 'primary' : undefined}
          onClick={search}
          aria-label={formatMessage(messages.ariaLabelButton)}
          data-testid="searchfield-button"
        />
        {focused && jwtToken && !isolated && (
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <MenuList
                data-testid="searchfield-popover-list"
                sx={{
                  py: 0,
                  bgcolor: 'grey.light',
                  boxShadow: 1,
                  position: 'absolute',
                  zIndex: (theme) => theme.zIndex.appBar + 2, // higher than the flyout navigation (filter popover is appbar, appbar + 1 is flyout navigation)
                  top: '100%',
                  left: 0,
                  width: relevantPopup === 'suggestions' ? ['100%', '100%', '170%'] : '100%',
                }}
                onClick={blur}
              >
                {relevantPopup === 'suggestions' && (
                  <SearchSuggestions
                    jwtToken={jwtToken}
                    cursor={cursor}
                    onAccept={(
                      selectedSuggestion?: Suggestion,
                      allSuggestions?: Result[],
                      suggestionType?: GlycerinSuggestionType,
                    ): void => {
                      dispatchSearchGtmEvent(
                        value || displayValue,
                        selectedSuggestion,
                        allSuggestions,
                        suggestionType,
                      );
                      onSearch?.();
                    }}
                    setValue={externalChangeValue}
                    searchValue={debouncedValue}
                  />
                )}
                {relevantPopup === 'history' && (
                  <SearchHistory
                    jwtToken={jwtToken}
                    cursor={cursor}
                    onAccept={(acceptedValue) => {
                      updateInput(acceptedValue);
                      onSearch?.();
                    }}
                    setValue={externalChangeValue}
                  />
                )}
              </MenuList>
            </Suspense>
          </ErrorBoundary>
        )}
      </Box>
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default SearchField;
